import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Building2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useUser } from '@/hooks/use-user';

const POST_REGISTER_BUSINESS_PROMPT_KEY = 'post_register_add_business_prompt';

const OPEN_DELAY_MS = 2000;

export function PostRegisterBusinessPrompt() {
  const { user, isLoading } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (isLoading || !user) return;

    let cancelled = false;

    const clearFlag = () => {
      try {
        sessionStorage.removeItem(POST_REGISTER_BUSINESS_PROMPT_KEY);
      } catch {
        // ignore
      }
    };

    const readFlag = () => {
      try {
        return sessionStorage.getItem(POST_REGISTER_BUSINESS_PROMPT_KEY) === 'true';
      } catch {
        return false;
      }
    };

    if (!readFlag()) return;

    const businesses = user.businesses ?? [];
    if (businesses.length > 0) {
      clearFlag();
      return;
    }

    const communities = user.communities ?? [];
    const businessRelevant =
      communities.length === 0 ||
      communities.some((c) => c.isBusinessEnabled || c.isBusinessDirectoryEnabled);
    if (!businessRelevant) {
      clearFlag();
      return;
    }

    if (location.pathname.includes('/create/business')) {
      clearFlag();
      return;
    }

    const t = window.setTimeout(() => {
      if (cancelled) return;
      if (!readFlag()) return;
      if ((user.businesses ?? []).length > 0) {
        clearFlag();
        return;
      }
      setIsOpen(true);
    }, OPEN_DELAY_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [user, isLoading, location.pathname]);

  const dismissAndClear = () => {
    try {
      sessionStorage.removeItem(POST_REGISTER_BUSINESS_PROMPT_KEY);
    } catch {
      // ignore
    }
    setIsOpen(false);
  };

  const handleAddBusiness = () => {
    dismissAndClear();
    navigate('/create/business');
  };

  const handleLater = () => {
    dismissAndClear();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && dismissAndClear()}>
      <DialogContent className="max-w-md gap-0 overflow-hidden border bg-background p-0 shadow-lg sm:rounded-2xl">
        <DialogHeader className="gap-3 p-6 pb-2">
          <div className="flex size-11 items-center justify-center rounded-2xl bg-primary/10 text-primary shadow-inner">
            <Building2 className="size-6" aria-hidden />
          </div>
          <DialogTitle className="bg-gradient-to-r from-primary to-blue-400 bg-clip-text text-left text-2xl font-bold text-transparent">
            Add your business?
          </DialogTitle>
          <DialogDescription className="text-left text-base leading-relaxed">
            List your company in the directory and make it easier for members to discover you. You
            can add or edit details anytime from your profile.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col gap-2 border-t bg-muted/30 p-6 sm:flex-col sm:space-x-0">
          <Button
            type="button"
            className="w-full rounded-xl shadow-primary/25"
            onClick={handleAddBusiness}
          >
            Add business details
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="w-full rounded-xl text-muted-foreground"
            onClick={handleLater}
          >
            Not now — I&apos;ll do this later
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
